import {useMemo} from 'react';
import {ExtendedTreeViewItem} from '../config/explorer-mrt-types';
import {ResourceType} from '../../../../@types/resource-type';

/**
 * Hook to filter tree data based on search text
 * Returns filtered data that includes matching items and their ancestors
 */
export function useTreeFilter(
    data: ExtendedTreeViewItem[],
    searchText: string
): {
    filteredData: ExtendedTreeViewItem[];
    matchingIds: Set<string>;
} {
    return useMemo(() => {
        if (!searchText || searchText.trim() === '') {
            return {
                filteredData: data,
                matchingIds: new Set<string>(),
            };
        }

        const searchLower = searchText.toLowerCase();
        const matchingIds = new Set<string>();
        const ancestorIds = new Set<string>();

        // First pass: find all matching items at any level
        // Returns true if any match was found in this subtree
        const findMatches = (items: ExtendedTreeViewItem[], parentPath: string[] = []): boolean => {
            let hasMatchInSubtree = false;

            items.forEach(item => {
                // Check if item name matches
                const nameMatches = item.name.toLowerCase().includes(searchLower);

                if (nameMatches) {
                    matchingIds.add(item.id);
                    parentPath.forEach(ancestorId => ancestorIds.add(ancestorId));
                    hasMatchInSubtree = true;
                }

                // Recursively search children
                if (item.children && item.children.length > 0) {
                    const childHasMatch = findMatches(item.children, [...parentPath, item.id]);

                    // If any child matched and this is a safety model, mark it as matching too
                    // This ensures the safety model is highlighted when its event sequences match
                    if (childHasMatch && item.type === ResourceType.SAFETY_MODELS && !matchingIds.has(item.id)) {
                        matchingIds.add(item.id);
                        parentPath.forEach(ancestorId => ancestorIds.add(ancestorId));
                    }

                    if (childHasMatch) {
                        hasMatchInSubtree = true;
                    }
                }
            });

            return hasMatchInSubtree;
        };

        findMatches(data);

        // Second pass: build filtered tree including matches and their ancestors
        const filterTree = (items: ExtendedTreeViewItem[]): ExtendedTreeViewItem[] => {
            return items
                .filter(item => {
                    // Include item if it matches or if it's an ancestor of a match
                    return matchingIds.has(item.id) || ancestorIds.has(item.id);
                })
                .map(item => {
                    // If item has children, recursively filter them
                    if (item.children && item.children.length > 0) {
                        // For matching groups or safety models, keep all children unfiltered
                        const isMatchingGroup = item.type === ResourceType.GROUPS && matchingIds.has(item.id);
                        const isMatchingSafetyModel = item.type === ResourceType.SAFETY_MODELS && matchingIds.has(item.id);

                        if (isMatchingGroup || isMatchingSafetyModel) {
                            return item;
                        }

                        const filteredChildren = filterTree(item.children);

                        // Only include the item if it has matching descendants or is a match itself
                        if (filteredChildren.length > 0 || matchingIds.has(item.id)) {
                            return {
                                ...item,
                                children: filteredChildren,
                            };
                        }
                    }

                    return item;
                })
                .filter(Boolean); // Remove any undefined items
        };

        const filteredData = filterTree(data);

        return {
            filteredData,
            matchingIds,
        };
    }, [data, searchText]);
}

/**
 * Get all item IDs that should be expanded to show search results
 * When searching, we want to expand:
 * 1. Every node that contains a match or has matching descendants
 * 2. Every node along the path from root to any match
 * This ensures all matches are visible without manual expansion
 */
export function getExpandedIdsForSearch(
    data: ExtendedTreeViewItem[],
    matchingIds: Set<string>
): Set<string> {
    const expandedIds = new Set<string>();

    // Recursively process tree and collect IDs to expand
    const processTree = (items: ExtendedTreeViewItem[]): boolean => {
        let hasMatchInBranch = false;

        items.forEach(item => {
            const isDirectMatch = matchingIds.has(item.id);
            let hasMatchingDescendant = false;

            // Check children recursively
            if (item.children && item.children.length > 0) {
                hasMatchingDescendant = processTree(item.children);
            }

            // This item should be expanded if:
            // 1. It has matching descendants, OR
            // 2. It is a match itself and has children
            if (hasMatchingDescendant || (isDirectMatch && item.children && item.children.length > 0)) {
                expandedIds.add(item.id);
            }

            // Report up if this branch has any matches
            if (isDirectMatch || hasMatchingDescendant) {
                hasMatchInBranch = true;
            }
        });

        return hasMatchInBranch;
    };

    processTree(data);

    return expandedIds;
}